import { mutate } from './api'
import { extrasOf, type Extra } from './extras'
import { rememberedEach, remember, type AskedExtra } from './done-extras'
import { currencySymbol } from './round-config'
import { el, money, parseMoney } from './ui'

// A helper's extras waiting for the owner's price (0091; Liam, 27 Oct 2026; the drawings' E7): at the top of Worker
// notes, one card a visit: who and when, each extra with a price box (their usual insides, the price last used for
// gutters, £15 a panel), then "Charge £70.00" or "No charge". Charge puts them into the visit's price as the owner's own
// Extras? does (inside_pence, extras_json, amount_pence) and clears the wait; No charge clears it and keeps a line in
// the visit's note. Both with Undo. A visit already on an invoice can't change: it says so and only clears the wait.

export interface AskedVisit {
  id: number
  version: number
  customer_id: number
  customer_name: string
  date: string
  extras_asked_json: string
  extras_asked_at: string | null
  extras_asked_by_name: string | null
  amount_pence: number | null
  inside_pence: number | null
  extras_json: string | null
  payment_status: string | null
  payment_method: string | null
  customer_inside_price_pence: number | null
  invoiced: number
}

function askedOf(json: string): AskedExtra[] {
  try { const v = JSON.parse(json); return Array.isArray(v) ? v : [] } catch { return [] }
}
const startEach = (v: AskedVisit, a: AskedExtra): number | null =>
  a.kind === 'insides' ? v.customer_inside_price_pence ?? null : a.kind === 'other' ? null : rememberedEach(a.kind) ?? (a.kind === 'conservatory' ? 1500 : null)

export function askedCard(v: AskedVisit, when: string, onDone: (words: string, undo: () => Promise<void>) => void, onError: (words: string) => void): HTMLElement {
  const asked = askedOf(v.extras_asked_json)
  const boxes = asked.map((a) => {
    const input = el('input', { type: 'text', inputmode: 'decimal', class: 'wd-extra-price', 'aria-label': `${a.name} price${a.kind === 'conservatory' ? ' a panel' : ''}`, placeholder: 'Price' }) as HTMLInputElement
    const each = startEach(v, a)
    if (each !== null) input.value = (each / 100).toFixed(2)
    return input
  })
  const eachPence = (i: number): number | null => { try { return parseMoney(boxes[i].value) } catch { return null } }
  const sum = (): number | null => asked.reduce<number | null>((s, a, i) => { const e = eachPence(i); return s === null || e === null ? null : s + e * a.count }, 0)
  const charge = el('button', { type: 'button', class: 'btn-primary extras-asked-charge' }) as HTMLButtonElement
  const none = el('button', { type: 'button', class: 'btn-secondary' }, v.invoiced ? 'Clear it' : 'No charge') as HTMLButtonElement
  const paint = (): void => { const t = sum(); charge.textContent = t === null ? 'Set the prices' : `Charge ${money(t)}`; charge.disabled = t === null }
  boxes.forEach((b) => b.addEventListener('input', paint))
  paint()
  const path = `/api/cleans/${v.id}`
  const before = { amount_pence: v.amount_pence, inside_pence: v.inside_pence, extras_json: extrasOf(v.extras_json).length ? extrasOf(v.extras_json) : null }
  const back = (version: number, extra: Record<string, unknown> = {}): Promise<void> =>
    mutate({ label: `Undo extras: ${v.customer_name} ${v.date}`, method: 'PATCH', path, body: { version, extras_asked: asked, ...extra } }).then(() => undefined)
  const busy = (on: boolean): void => { charge.disabled = on || sum() === null; none.disabled = on }

  charge.addEventListener('click', async () => {
    const t = sum()
    if (t === null || v.amount_pence === null) { onError(v.amount_pence === null ? `${v.customer_name}'s visit has no price yet. Put its price on first.` : 'Put in a price for each extra.'); return }
    let inside = v.inside_pence ?? 0
    const list: Extra[] = extrasOf(v.extras_json).map((e) => ({ ...e }))
    asked.forEach((a, i) => {
      const e = eachPence(i) as number
      if (a.kind === 'insides') inside += e
      else { list.push({ kind: a.kind, name: a.name, count: a.count, each_pence: e }); if (a.kind !== 'other') remember(a.kind, e) }
    })
    busy(true)
    const ack = await mutate<{ version?: number }>({ label: `Charge extras: ${v.customer_name} ${v.date}`, method: 'PATCH', path,
      body: { version: v.version, extras_asked: null, amount_pence: v.amount_pence + t, inside_pence: inside > 0 ? inside : null, extras_json: list.length ? list : null } })
    if (!ack.ok && ack.status !== 0) { busy(false); onError(ack.status === 409 ? `${v.customer_name}'s visit changed on another phone. Close this and open it again.` : ack.error ?? "That didn't save."); return }
    const version = ack.row?.version ?? v.version + 1
    onDone(`Charged ${money(t)} on ${v.customer_name}`, () => back(version, before))
  })
  none.addEventListener('click', async () => {
    busy(true)
    const said = asked.map((a) => a.kind === 'conservatory' && a.count > 1 ? `${a.name} (${a.count} panels)` : a.name).join(', ')
    const ack = await mutate<{ version?: number }>({ label: `No charge: ${v.customer_name} ${v.date}`, method: 'PATCH', path,
      body: { version: v.version, extras_asked: null, note_append: `${v.extras_asked_by_name ?? 'They'} also did: ${said} (no charge).` } })
    if (!ack.ok && ack.status !== 0) { busy(false); onError(ack.error ?? "That didn't save."); return }
    const version = ack.row?.version ?? v.version + 1
    onDone(v.invoiced ? `Cleared: ${v.customer_name}` : `No charge: ${v.customer_name}`, () => back(version))
  })

  const rows = el('div', { class: 'extras-asked-rows' }, ...asked.map((a, i) => el('label', { class: 'wd-extra-row' },
    el('strong', { class: 'wd-extra-label' }, a.kind === 'conservatory' ? `${a.name} · ${a.count} panel${a.count === 1 ? '' : 's'}` : a.name),
    el('span', { class: 'wd-extra-each' }, el('span', {}, `${currencySymbol()}${a.kind === 'conservatory' ? ' a panel' : ''}`), boxes[i]))))
  const name = el('a', { class: 'worker-notes-customer', href: `#/customer/${v.customer_id}` }, v.customer_name)
  const card = el('article', { class: 'worker-notes-item extras-asked-item' },
    el('div', { class: 'worker-notes-item-head' }, name, el('span', { class: 'worker-notes-who' }, [v.extras_asked_by_name?.trim(), when].filter(Boolean).join(' · '))),
    el('span', { class: 'worker-notes-ask' }, 'Extras they did'))
  if (v.invoiced) {
    card.append(el('p', { class: 'muted' }, "This visit is on an invoice, so its price can't change here. Cancel or credit that invoice first, or clear this."))
    card.append(el('div', { class: 'worker-notes-acts' }, none))
    return card
  }
  card.append(rows)
  if (v.payment_status === 'paid') card.append(el('p', { class: 'muted' }, v.payment_method === 'bank'
    ? 'Paid by bank already. Charged, the visit still shows paid: ask them for the extra.'
    : 'Paid already. Charged, what they still owe goes on Owed.'))
  card.append(el('div', { class: 'worker-notes-acts extras-asked-acts' }, charge, none))
  return card
}

// A helper's parking (0091; the drawings' W2): what the customer has now beside what they found, "Use it" (onto the
// customer's Parking, guarded by the customer's version, with Undo) or "Not now" (their words kept as a line in that
// visit's note). Either way the visit's wait is cleared.
export interface ParkingAsked { id: number; version: number; customer_id: number; customer_name: string; customer_version: number; date: string; parking_asked: string; parking_asked_at: string | null; parking_now: string | null; parking_asked_by_name: string | null }

export function parkingCard(p: ParkingAsked, when: string, onDone: (words: string, undo: () => Promise<void>) => void, onError: (words: string) => void): HTMLElement {
  const use = el('button', { type: 'button', class: 'btn-primary' }, 'Use it') as HTMLButtonElement
  const not = el('button', { type: 'button', class: 'btn-secondary' }, 'Not now') as HTMLButtonElement
  const cleanPath = `/api/cleans/${p.id}`, custPath = `/api/customers/${p.customer_id}`
  const busy = (on: boolean): void => { use.disabled = on; not.disabled = on }
  const clearWait = (version: number, extra: Record<string, unknown> = {}) => mutate<{ version?: number }>({ label: `Parking from ${p.parking_asked_by_name ?? 'a helper'}: ${p.customer_name}`, method: 'PATCH', path: cleanPath, body: { version, parking_asked: null, ...extra } })
  use.addEventListener('click', async () => {
    busy(true)
    const ack = await mutate<{ version?: number }>({ label: `Parking: ${p.customer_name}`, method: 'PATCH', path: custPath, body: { version: p.customer_version, parking_restrictions: p.parking_asked } })
    if (!ack.ok && ack.status !== 0) { busy(false); onError(ack.status === 409 ? `${p.customer_name}'s parking changed since. Close this and open it again to compare.` : ack.error ?? "That didn't save."); return }
    const cleared = await clearWait(p.version)
    const custVersion = ack.row?.version ?? p.customer_version + 1, cleanVersion = cleared.row?.version ?? p.version + 1
    onDone(`Parking changed for ${p.customer_name}`, async () => {
      await mutate({ label: `Undo parking: ${p.customer_name}`, method: 'PATCH', path: custPath, body: { version: custVersion, parking_restrictions: p.parking_now } })
      await mutate({ label: `Parking back: ${p.customer_name}`, method: 'PATCH', path: cleanPath, body: { version: cleanVersion, parking_asked: p.parking_asked } })
    })
  })
  not.addEventListener('click', async () => {
    busy(true)
    const ack = await clearWait(p.version, { note_append: `Parking, from ${p.parking_asked_by_name ?? 'a helper'}: ${p.parking_asked}` })
    if (!ack.ok && ack.status !== 0) { busy(false); onError(ack.error ?? "That didn't save."); return }
    const v = ack.row?.version ?? p.version + 1
    onDone(`Kept as a note on ${p.customer_name}'s visit`, async () => { await mutate({ label: `Parking back: ${p.customer_name}`, method: 'PATCH', path: cleanPath, body: { version: v, parking_asked: p.parking_asked } }) })
  })
  const name = el('a', { class: 'worker-notes-customer', href: `#/customer/${p.customer_id}` }, p.customer_name)
  return el('article', { class: 'worker-notes-item extras-asked-item' },
    el('div', { class: 'worker-notes-item-head' }, name, el('span', { class: 'worker-notes-who' }, [p.parking_asked_by_name?.trim(), when].filter(Boolean).join(' · '))),
    el('span', { class: 'worker-notes-ask' }, 'Parking they found'),
    el('div', { class: 'parking-compare' },
      el('div', { class: 'parking-compare-now' }, el('small', {}, 'Now'), el('strong', {}, p.parking_now?.trim() || 'Nothing saved')),
      el('div', { class: 'parking-compare-new' }, el('small', {}, `${p.parking_asked_by_name ?? 'They'} says`), el('strong', {}, p.parking_asked))),
    el('p', { class: 'muted' }, 'Use it changes their parking for every visit (Undo after). Not now keeps it as a note on that visit only.'),
    el('div', { class: 'worker-notes-acts extras-asked-acts' }, use, not))
}
